/**
 * Settings Constants
 * Mirrors Angular settings key names for ABP settings API
 */

/**
 * Workgroup Settings Keys
 */
export const WORKGROUP_SETTINGS = {
  INTERNAL_GROUP_NAME: 'motr.Settings.InternalGroupName',
  EXTERNAL_GROUP_NAME: 'motr.Settings.ExternalGroupName',
  CLIENT_GROUP_NAME: 'motr.Settings.ClientGroupName',
  PROCESS_GROUP_NAME: 'motr.Settings.ProcessGroupName',
  VENDOR_GROUP_NAME: 'motr.Settings.VendorGroupName',
  JOB_START_TIME: 'motr.Settings.JobStartTime',
  DATE_FORMAT: 'motr.Settings.DateFormat',
  TIME_FORMAT: 'motr.Settings.TimeFormat',
  DURATION_FORMAT: 'motr.Settings.DurationFormat',
} as const;

/**
 * New Client User Settings Keys
 */
export const NEW_CLIENT_USER_SETTINGS = {
  DEFAULT_WORKGROUP: 'motr.NewClientUser.DefaultWorkgroup',
  NOTIFICATION_TO_EMAIL: 'motr.NewClientUser.NotificationToEmail',
} as const;

/**
 * User Personal Settings Keys
 */
export const USER_SETTINGS = {
  SHOW_DASHBOARD_CHARTS: 'motr.User.ShowDashboardCharts',
  JOB_EMAILS_ENABLED: 'motr.User.JobEmailsEnabled',
  RECEIVE_CHECKLIST_EMAILS: 'motr.User.ReceiveChecklistEmails',
  FIRST_WARNING_DUE_DAYS: 'motr.User.firstWarningDueDays',
  SECOND_WARNING_DUE_DAYS: 'motr.User.secondWarningDueDays',
  DEFAULT_LANDING_PAGE: 'motr.User.DefaultLandingPage',
  CLIENT_DASHBOARD_LANDING_PAGE: 'motr.User.ClientDashboardLandingPage',
  STALE_DAYS_THRESHOLD: 'motr.User.StaleDaysThreshold',
} as const;

/** Stored value prefix for a specific motr-Q Client Dashboard landing page. */
export const CLIENT_QUESTIONNAIRE_LANDING_PREFIX = 'front-door:';
export const INTERNAL_QUESTIONNAIRE_LANDING_PREFIX = 'dashboard-questionnaire:';

export function getClientQuestionnaireLandingValue(questionnaireId: string): string {
  return `${CLIENT_QUESTIONNAIRE_LANDING_PREFIX}${questionnaireId}`;
}

export function getClientQuestionnaireId(landingValue: string | null | undefined): string | null {
  if (!landingValue?.startsWith(CLIENT_QUESTIONNAIRE_LANDING_PREFIX)) return null;
  return landingValue.slice(CLIENT_QUESTIONNAIRE_LANDING_PREFIX.length) || null;
}

export function getInternalQuestionnaireLandingValue(questionnaireId: string): string {
  return `${INTERNAL_QUESTIONNAIRE_LANDING_PREFIX}${questionnaireId}`;
}

export function getInternalQuestionnaireId(landingValue: string | null | undefined): string | null {
  if (!landingValue?.startsWith(INTERNAL_QUESTIONNAIRE_LANDING_PREFIX)) return null;
  return landingValue.slice(INTERNAL_QUESTIONNAIRE_LANDING_PREFIX.length) || null;
}

/**
 * Tenant Admin Settings Keys
 * Names must match exactly what's defined in Angular/backend
 */
export const TENANT_SETTINGS = {
  WEB_BASE_URL: 'motr.Settings.WebBaseUrl',
  TEST_EMAIL: 'motr.Settings.TestEmail',
  BLOB_STORAGE_URL: 'motr.Settings.BlobStorageUrl',
  BLOB_STORAGE_CONNECTION_STRING: 'motr.Settings.BlobStorageConnectionString',
  SERVICE_REQUEST_RECEIVER_EMAIL: 'motr.ServiceRequest.ReceiverEmail',
  SERVICE_REQUEST_ALLOWED_SENDER_DOMAINS: 'motr.ServiceRequest.AllowedSenderDomains',
  AI_ASSISTANT_API_KEY: 'motr.AI.AssistantAPIKey',
  AI_ASSISTANT_ID: 'motr.AI.AssistantId',
  OPENAI_MODEL_NAME: 'motr.openai.modelname',
  CSS_OVERRIDE: 'motr.Settings.CSSOverride',
  LOADING_MESSAGES: 'motr.Settings.LoadingMessages',
  AI_CAPABILITIES: 'motr.Settings.AiCapabilities',
  CHECKLIST_ITEM_LABELS: 'motr.Settings.CheckListItemLabels',
  BLOCK_UI_ENDPOINTS: 'motr-blockUI-endpoints',
  PREFER_ND_SERVICE_ACCOUNT_URLS: 'motr.PreferNDServiceAccount.Urls',
  COMMENT_INFO_TEXT: 'motr-comment-info-text',
  THEME_OVERRIDE: 'motr.Settings.ThemeOverride',
} as const;

/**
 * Power BI Settings Keys
 */
export const POWERBI_SETTINGS = {
  TENANT_ID: 'motr.PowerBI.TenantId',
  CLIENT_ID: 'motr.PowerBI.ClientId',
  CLIENT_SECRET: 'motr.PowerBI.ClientSecret',
} as const;

/**
 * AI Settings Keys
 */
export const AI_SETTINGS = {
  ALLOW_AI_TO_PROCESS_DOCUMENTS: 'motr.Settings.AllowAiToProcessDocuments',
} as const;

/**
 * motrQ Settings Keys
 */
export const MOTRQ_SETTINGS = {
  BASE_URL: 'motr.motrQ.BaseUrl',
  QUESTIONNAIRES: 'motr.motrQ.Questionnaires',
} as const;

/**
 * Chess Timer (Step Responsibility) Settings Keys
 */
export const CHESS_TIMER_SETTINGS = {
  US_LABEL: 'motr.Settings.ChessTimerUsLabel',
  THEM_LABEL: 'motr.Settings.ChessTimerThemLabel',
} as const;

/**
 * Get all chess timer setting keys as comma-separated string
 */
export function getChessTimerSettingKeys(): string {
  return combineSettingKeys(...Object.values(CHESS_TIMER_SETTINGS));
}

/**
 * DocuSign Settings Keys
 */
export const DOCUSIGN_SETTINGS = {
  REDIRECT_URL: 'motr.DocuSign.redirectUrl',
  BASE_API_URL: 'motr.DocuSign.baseApiUrl',
  CLIENT_ID: 'motr.DocuSign.clientId',
  CLIENT_SECRET: 'motr.DocuSign.clientSecret',
  AUTH_SERVER: 'motr.DocuSign.AuthServer',
} as const;

/**
 * Document Repository Settings Keys
 * Names match exactly what's used in Angular/backend
 */
export const DOCUMENT_REPOSITORY_SETTINGS = {
  // Default Repository Selection
  DEFAULT_REPOSITORY: 'motr.DocumentRepository',
  ENABLED_REPOSITORIES: 'motr.NetDocument.EnabledRepositories',

  // NetDocuments Settings
  ND_BASE_URL: 'motr.NetDocument.BaseUrl',
  ND_REDIRECT_URI: 'motr.NetDocument.RedirectURI',
  ND_BASE_LOGIN_URI: 'motr.NetDocument.BaseLoginURI',
  ND_CLIENT_ID: 'motr.NetDocument.ClientId',
  ND_CLIENT_SECRET: 'motr.NetDocument.ClientSecret',
  ND_REPOSITORY_ID: 'motr.NetDocument.NDRepositoryId',
  ND_DEFAULT_FOLDER: 'motr.NetDocuments.DefaultFolder',
  ND_UPLOAD_LOCATIONS: 'motr.NetDocuments.UploadLocations',

  // iManage Settings
  IM_BASE_URL: 'motr.iManage.BaseUrl',
  IM_CLIENT_ID: 'motr.iManage.ClientId',
  IM_CLIENT_SECRET: 'motr.iManage.ClientSecret',
  IM_SERVICE_ACCOUNT_ID: 'motr.iManage.ServiceAccountId',
  IM_SERVICE_ACCOUNT_PASSWORD: 'motr.iManage.ServiceAccountPassword',
  IM_DEFAULT_FOLDER: 'motr.iManage.DefaultFolder',
  IM_UPLOAD_LOCATIONS: 'motr.iManage.UploadLocations',

  // Google Drive Settings
  GD_CLIENT_ID: 'motr.GoogleDrive.ClientId',
  GD_CLIENT_SECRET: 'motr.GoogleDrive.ClientSecret',
  GD_REDIRECT_URL: 'motr.GoogleDrive.RedirectUrl',
  GD_API_KEY: 'motr.GoogleDrive.ApiKey',
  GD_USE_SHARED: 'motr.GoogleDrive.UseSharedGoogleDrive',
  GD_SA_PROJECT_ID: 'motr.GoogleDrive.ServiceAccount.ProjectId',
  GD_SA_PRIVATE_KEY_ID: 'motr.GoogleDrive.ServiceAccount.PrivateKeyId',
  GD_SA_PRIVATE_KEY: 'motr.GoogleDrive.ServiceAccount.PrivateKey',
  GD_SA_CLIENT_EMAIL: 'motr.GoogleDrive.ServiceAccount.ClientEmail',
  GD_SA_CLIENT_ID: 'motr.GoogleDrive.ServiceAccount.ClientId',
  GD_SA_ROOT_FOLDER_ID: 'motr.GoogleDrive.ServiceAccount.RootFolderId',
  GD_DEFAULT_FOLDER: 'motr.GoogleDrive.DefaultFolder',
  GD_UPLOAD_LOCATIONS: 'motr.GoogleDrive.UploadLocations',

  // SharePoint Online Settings
  SP_USE_SHARED: 'motr.SharePointOnline.UseSharedSharepointOnline',
  SP_SITE_URL: 'motr.SharePointOnline.SiteUrl',
  SP_CLIENT_ID: 'motr.SharePointOnline.ClientId',
  SP_TENANT_ID: 'motr.SharePointOnline.TenantId',
  SP_THUMBPRINT: 'motr.SharePointOnline.Thumbprint',
  SP_PFX_CERTIFICATE_BASE64: 'motr.SharePointOnline.PfxCertificateBase64',
  SP_PFX_CERTIFICATE_PASSWORD: 'motr.SharePointOnline.PfxCertificatePassword',
  SP_DEFAULT_FOLDER: 'motr.SharePointOnline.DefaultFolder',
  SP_UPLOAD_LOCATIONS: 'motr.SharePointOnline.UploadLocations',

  // Azure Blob Settings
  AB_USE_SHARED: 'motr.AzureBlob.UseSharedAzureBlob',
  AB_CONNECTION_STRING: 'motr.AzureBlob.ConnectionString',
  AB_DEFAULT_FOLDER: 'motr.AzureBlob.DefaultFolder',
  AB_UPLOAD_LOCATIONS: 'motr.AzureBlob.UploadLocations',
} as const;

/**
 * Permissions for settings tabs
 */
export const SETTINGS_PERMISSIONS = {
  // Main settings tabs
  SETTINGS: 'motr.Settings.Settings',
  DOCUMENT_MANAGEMENT: 'motr.Settings.DocumentManagement',
  NETDOCUMENTS_SETTINGS: 'motr.Settings.NetDocumentsSettings',
  POWERBI_REPORTS: 'motr.Reports.AllowManagePowerBIReports',
  TENANT_SETTINGS: 'motr.Application.AllowManageTenantSettings',
  DOCUSIGN: 'motr.Settings.DocuSign',
  MOTRQ_QUESTIONNAIRE: 'motr.motrQ.ManageQuestionnaire',
  API_KEYS: 'motr.ApiKeys',
  ENTITY_TYPES: 'motr.EntityTypes',

  // Feature-specific permissions
  CSS_OVERRIDE: 'motr.Application.CSSOverride',
  CACHE_CLEANING: 'motr.Application.AllowCacheCleaning',
  RESET_ROLE_PERMISSIONS: 'motr.Application.AllowResetRolePermissions',
  ND_SERVICE_ACCOUNT: 'motr.Application.HasNDServiceAccountAccess',
  IMANAGE_SERVICE_ACCOUNT: 'motr.Application.HasiManageServiceAccountAccess',
  GOOGLE_SERVICE_ACCOUNT: 'motr.Application.AllowGoogleServiceAccountAccess',
  IMANAGE_CONNECTION: 'motr.Application.AllowiManageConnection',
  ND_CONNECTION: 'motr.Application.AllowNDConnection',
  DOCUMENT_REPOSITORY_SYNC: 'motr.Application.AllowDocumentRepositorySync',

  // Cabinet CRUD permissions
  CABINET_CREATE: 'motr.NetDocumentSettings.Create',
  CABINET_EDIT: 'motr.NetDocumentSettings.Edit',
  CABINET_DELETE: 'motr.NetDocumentSettings.Delete',
} as const;

/**
 * Helper to combine setting keys into comma-separated string
 */
export function combineSettingKeys(...keys: string[]): string {
  return keys.join(',');
}

/**
 * Get all workgroup setting keys as comma-separated string
 */
export function getWorkgroupSettingKeys(): string {
  return combineSettingKeys(
    ...Object.values(WORKGROUP_SETTINGS),
    ...Object.values(NEW_CLIENT_USER_SETTINGS)
  );
}

/**
 * Get all user setting keys as comma-separated string
 */
export function getUserSettingKeys(): string {
  return combineSettingKeys(...Object.values(USER_SETTINGS));
}

/**
 * Get all tenant setting keys as comma-separated string
 */
export function getTenantSettingKeys(): string {
  return combineSettingKeys(...Object.values(TENANT_SETTINGS));
}

/**
 * Get all PowerBI setting keys as comma-separated string
 */
export function getPowerBISettingKeys(): string {
  return combineSettingKeys(...Object.values(POWERBI_SETTINGS));
}

/**
 * Get all AI setting keys as comma-separated string
 */
export function getAISettingKeys(): string {
  return combineSettingKeys(...Object.values(AI_SETTINGS));
}

/**
 * Get all motrQ setting keys as comma-separated string
 */
export function getMotrQSettingKeys(): string {
  return combineSettingKeys(...Object.values(MOTRQ_SETTINGS));
}

/**
 * Get all document repository setting keys as comma-separated string
 */
export function getDocumentRepositorySettingKeys(): string {
  return combineSettingKeys(...Object.values(DOCUMENT_REPOSITORY_SETTINGS));
}

/**
 * Get all DocuSign setting keys as comma-separated string
 */
export function getDocuSignSettingKeys(): string {
  return combineSettingKeys(...Object.values(DOCUSIGN_SETTINGS));
}

/**
 * Document repository provider types
 */
export const DOCUMENT_PROVIDERS_LIST = [
  { value: 'NoRepository', label: 'No Repository' },
  { value: 'NetDocuments', label: 'NetDocuments' },
  { value: 'iManage', label: 'iManage' },
  { value: 'GoogleDrive', label: 'Google Drive' },
  { value: 'SharePointOnline', label: 'SharePoint Online' },
  { value: 'AzureBlob', label: 'Azure Blob' },
] as const;

/**
 * Default repository options for radio buttons
 */
export const DEFAULT_REPOSITORY_OPTIONS = [
  { value: 'NoRepository', label: 'No Repository' },
  { value: 'NetDocuments', label: 'NetDocuments' },
  { value: 'iManage', label: 'iManage' },
  { value: 'GoogleDrive', label: 'Google Drive' },
  { value: 'SharePointOnline', label: 'SharePoint Online' },
  { value: 'AzureBlob', label: 'Azure Blob' },
] as const;
